import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef } from "react";
import {
  ENTITY_ACTIONS,
  FILE_ENTITY_MAP,
  createSyncApi,
  type FileEntityName,
} from "@repo/services";
import { fileApi } from "~/api";

type SignalRContextType = ReturnType<typeof createSyncApi> | null;

const FileSignalRContext = createContext<SignalRContextType>(null);

const getQueryData = (oldData: unknown, action: string, newEntity: { id?: unknown }) => {
  if (!Array.isArray(oldData)) return oldData;
  const entityId = newEntity?.id;

  switch (action) {
    case ENTITY_ACTIONS.Created:
      if (oldData.some((item) => item.id === entityId)) return oldData;
      return [...oldData, newEntity];
    case ENTITY_ACTIONS.Updated:
    case ENTITY_ACTIONS.Complete:
      return oldData.map((item) => (item.id === entityId ? newEntity : item));
    case ENTITY_ACTIONS.Deleted:
      return oldData.filter((item) => item.id !== entityId);
    default:
      return oldData;
  }
};

export const FileSignalRProvider = ({ children }: { children: React.ReactNode }) => {
  const apiRef = useRef(createSyncApi(import.meta.env.VITE_FILE_API_BASE_URL));
  const queryClient = useQueryClient();

  useEffect(() => {
    const api = apiRef.current;

    api.start().catch((err) => {
      console.error("[SignalR · File] Failed to start connection:", err);
    });

    api.connection.onclose((error) => {
      if (error) {
        console.error("[SignalR · File] Connection closed with error:", error);
      }
    });

    api.connection.onreconnecting((error) => {
      console.warn("[SignalR · File] Reconnecting...", error);
    });

    api.connection.onreconnected(() => {
      console.log("[SignalR · File] Reconnected.");
    });

    const removeListener = api.onEntityChanged((data) => {
      const { typeNameConstant, entity, action } = data;
      const typedEntity = entity as { id?: unknown; taskId?: number; brandId?: number };
      const apiPath = FILE_ENTITY_MAP[typeNameConstant as FileEntityName];

      if (!apiPath) return;
      let queryKey: readonly unknown[];

      try {
        if (apiPath.includes("{taskId}") && typedEntity?.taskId) {
          queryKey = fileApi.queryOptions(
            "get",
            apiPath as Parameters<typeof fileApi.queryOptions>[1],
            {
              params: {
                path: { taskId: typedEntity.taskId },
              },
            } as unknown as Parameters<typeof fileApi.queryOptions>[2]
          ).queryKey;
        } else {
          queryKey = fileApi.queryOptions(
            "get",
            apiPath as Parameters<typeof fileApi.queryOptions>[1]
          ).queryKey;
        }
      } catch (_error) {
        return;
      }

      queryClient.setQueryData(queryKey, (oldData: unknown) => {
        if (!oldData) {
          if (action === ENTITY_ACTIONS.Created) return [typedEntity];
          return undefined;
        }
        return getQueryData(oldData, action, typedEntity);
      });

      queryClient.invalidateQueries({ queryKey });
    });

    return () => {
      removeListener();
    };
  }, [queryClient]);

  return (
    <FileSignalRContext.Provider value={apiRef.current}>{children}</FileSignalRContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useFileSignalR = (): SignalRContextType => {
  const context = useContext(FileSignalRContext);
  if (!context) throw new Error("useFileSignalR must be used within FileSignalRProvider");
  return context;
};
